// src/pages/ReservationNew.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import { http } from '@/api/http';
import { useNavigate } from 'react-router-dom';
import placeholderCharacter from '@/assets/images/placeholders/character.jpg';

// MUI
import {
  Box,
  Grid,
  TextField,
  Button,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Typography,
  Autocomplete,
  Alert,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers';

// Ant Design Icons
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  ArrowLeftOutlined,
  DownOutlined
} from '@ant-design/icons';

// ===== Estilos =====
const UPPER_INPUT_SX = {
  '& .MuiInputBase-input, & .MuiAutocomplete-input': { textTransform: 'uppercase' },
  '& .MuiSelect-select': { textTransform: 'uppercase' },
  '& .MuiChip-label': { textTransform: 'uppercase' }
};

// Altura uniforme (todos los campos e iconos de la fila)
const FIELD_HEIGHT = 36;
const COMPACT_INPUT_SX = {
  '& .MuiOutlinedInput-root': { height: FIELD_HEIGHT },
  '& .MuiInputBase-input': { py: 0, height: FIELD_HEIGHT, boxSizing: 'border-box' },
  '& .MuiSelect-select': { py: 0, height: FIELD_HEIGHT, display: 'flex', alignItems: 'center' },
  '& .MuiInputLabel-root': { transform: 'translate(14px, 8px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' }
};

// ===== Calidad (DB) =====
// OJO: aquí sí usamos los valores reales de BD: estreno | media | baja
const QUALITY = [
  { label: 'Estreno', value: 'estreno' },
  { label: 'Segunda', value: 'media' },
  { label: 'Tercera', value: 'baja' }
];

// =========================
//  Modal: Picker de Personajes
// =========================
function CardImage({ src, alt }) {
  const FALLBACK = placeholderCharacter;
  return (
    <img
      src={src || FALLBACK}
      alt={alt || 'Personaje'}
      loading="lazy"
      onError={(e) => {
        if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK;
      }}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

function CharacterPickerModal({ open, onClose, onPick, alreadyIds }) {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (text) => {
    try {
      setLoading(true);
      const { data } = await http.get('/character-cards', { params: { q: text || '', limit: 18 } });
      setOptions(Array.isArray(data) ? data : []);
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) search('');
  }, [open, search]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Agregar por danza / personaje</DialogTitle>
      <DialogContent dividers>
        <TextField
          autoFocus
          label="Buscar (danza o personaje)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              search(q);
            }
          }}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="Buscar" onClick={() => search(q)} disabled={loading}>
                  <SearchOutlined />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />
        <Grid container spacing={2}>
          {options.map((opt) => {
            const disabled = alreadyIds.has?.(opt.id);
            return (
              <Grid item xs={12} sm={6} md={4} key={opt.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ position: 'relative', pt: '138%', overflow: 'hidden' }}>
                    <CardImage src={opt.img} alt={opt.title} />
                  </Box>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      noWrap
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {opt.title}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ textTransform: 'uppercase' }}
                    >
                      {opt.dance_name || 'Sin danza'}
                    </Typography>

                    {/* Chip de calidad si viene del backend */}
                    {opt.quality && (
                      <Chip
                        size="small"
                        label={
                          `${(QUALITY.find((q) => q.value === opt.quality)?.label) || opt.quality}`
                        }
                        color="info"
                        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
                      />
                    )}

                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PlusOutlined />}
                        onClick={() => onPick(opt)}
                        disabled={disabled}
                        fullWidth
                      >
                        {disabled ? 'Ya agregado' : 'Agregar'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {!loading && options.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Sin resultados.
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// =========================
//  ReservationNew (misma base que OrdersForm pero para separaciones)
// =========================
export default function ReservationNew() {
  const navigate = useNavigate();

  // ======= Cliente =======
  const [orderNumber, setOrderNumber] = useState('');
  const [orderNumberPreview, setOrderNumberPreview] = useState('');
  const [dni, setDni] = useState('');
  const [fullName, setFullName] = useState('');
  const [lastNameFather, setLastNameFather] = useState('');
  const [lastNameMother, setLastNameMother] = useState('');
  const [address, setAddress] = useState('');
  const [references, setReferences] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('EFECTIVO');
  const [paymentRef, setPaymentRef] = useState('');

  // ======= Datos de Reserva / Alquiler =======
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [warrantyType, setWarrantyType] = useState('');
  const [warrantyDetail, setWarrantyDetail] = useState('');
  const status = 'Activo';
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const [clientAlert, setClientAlert] = useState(null);

  // ======= Personajes y prendas =======
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedChars, setSelectedChars] = useState([]);
  const [charItemsCache, setCharItemsCache] = useState({});
  const [removedFromAuto, setRemovedFromAuto] = useState(() => new Set());
  const [extraLines, setExtraLines] = useState([]);
  const [availabilityCache, setAvailabilityCache] = useState({});
  const [showCharacters] = useState(true);

  // acordeones de personajes (todos cerrados al inicio)
  const [collapsedChars, setCollapsedChars] = useState(() => new Set());
  const isCharOpen = (key) => !collapsedChars.has(key);
  const handleCharAccordion = (key) => (_event, expanded) => {
    setCollapsedChars((prev) => {
      const next = new Set(prev);
      if (expanded) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ======= Pagos =======
  const [partial, setPartial] = useState(0);
  const [total, setTotal] = useState(0);
  const [balance, setBalance] = useState(0);

  useEffect(() => {
    const s = Number(total || 0) - Number(partial || 0);
    setBalance(isNaN(s) ? 0 : s);
  }, [partial, total]);

  const [searching, setSearching] = useState(false);

  // ======= N° siguiente (mismo endpoint que OrdersForm) =======
  useEffect(() => {
    let mounted = true;
    async function fetchNextNumber() {
      try {
        const { data } = await http.get('/orders/next-number');
        if (!mounted) return;
        if (data?.next_number) setOrderNumber(String(data.next_number));
        setOrderNumberPreview(data?.order_number || data?.next_number || '');
      } catch {
        /* ignore */
      }
    }
    fetchNextNumber();
    return () => {
      mounted = false;
    };
  }, []);

  // ======= Buscar DNI =======
  const handleSearchDni = async () => {
    if (!dni || dni.length < 8) {
      Swal.fire({ icon: 'error', title: 'DNI inválido', text: 'Ingresa un DNI de al menos 8 dígitos.' });
      return;
    }
    try {
      setSearching(true);
      const { data } = await http.get('/clients/by-dni', { params: { dni } });
      setFullName(data?.first_name ?? data?.full_name ?? '');
      setLastNameFather(data?.last_name_father ?? '');
      setLastNameMother(data?.last_name_mother ?? '');
      setAddress(data?.address ?? '');

      const a = data?.alerts || {};
      const lines = [];
      const hasPending = (a.pending_orders || 0) > 0;
      const hasOverdue = (a.overdue_orders || 0) > 0;
      const hasDebt = Number(a.outstanding_balance || 0) > 0;
      const hasNotRet = (a.not_returned_qty || 0) > 0;
      const hasDamaged = !!a.has_damaged_returns;
      const hasMissing = !!a.has_missing_returns;

      if (hasPending) lines.push(`• Órdenes pendientes: ${a.pending_orders}`);
      if (hasOverdue) lines.push(`• Órdenes vencidas: ${a.overdue_orders}`);
      if (hasDebt) lines.push(`• Deuda de alquiler: S/ ${Number(a.outstanding_balance).toFixed(2)}`);
      if (hasNotRet) lines.push(`• Prendas aún no devueltas: ${a.not_returned_qty}`);
      if (hasDamaged) lines.push('• Historial: devoluciones DAÑADAS');
      if (hasMissing) lines.push('• Historial: prendas FALTANTES');

      const isClean = !(hasPending || hasOverdue || hasDebt || hasNotRet || hasDamaged || hasMissing);
      if (isClean) {
        setClientAlert({
          severity: 'success',
          lines: ['Sin Antecedentes. No presenta deudas, devoluciones pendientes ni incidencias.']
        });
        Swal.fire({ icon: 'success', title: 'Cliente sin incidencias' });
      } else {
        setClientAlert({ severity: 'error', lines });
        Swal.fire({ icon: 'error', title: 'Atención con este cliente', html: lines.join('<br>') });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error al buscar DNI',
        text: err?.response?.data?.message || err?.message || String(err)
      });
    } finally {
      setSearching(false);
    }
  };

  // ======= Items por personaje =======
  const fetchCharItems = useCallback(
    async (charId) => {
      if (charItemsCache[charId]) return charItemsCache[charId];
      const { data } = await http.get(`/dance-characters/${charId}/items`);
      const items = Array.isArray(data) ? data : data?.data ?? [];
      setCharItemsCache((prev) => ({ ...prev, [charId]: items }));
      return items;
    },
    [charItemsCache]
  );

  const handlePickCharacter = async (opt) => {
    if (selectedChars.some((c) => c.id === opt.id)) {
      Swal.fire({ icon: 'info', title: 'Ya agregado', text: 'Ese personaje ya está en la lista.' });
      return;
    }

    setSelectedChars((prev) => [
      ...prev,
      {
        id: opt.id,
        name: opt.title,
        dance_id: opt.dance_id ?? null,
        dance_name: opt.dance_name,
        qty: 1,
        qtyDraft: '1',
        quality: opt.quality || opt.min_condition || null
      }
    ]);

    try {
      await fetchCharItems(opt.id);
    } catch {
      /* ignore */
    }
  };

  const removeCharacter = (id) => setSelectedChars((prev) => prev.filter((c) => c.id !== id));

  const handleCharQtyChange = (id, raw) => {
    const onlyDigits = (raw || '').replace(/[^\d]/g, '');
    setSelectedChars((prev) => prev.map((c) => (c.id === id ? { ...c, qtyDraft: onlyDigits } : c)));
  };

  const handleCharQtyCommit = (id) => {
    setSelectedChars((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const raw = (c.qtyDraft ?? String(c.qty ?? '')).trim();
        let n = raw === '' ? 0 : parseInt(raw, 10);
        if (isNaN(n) || n < 0) n = 0;
        return { ...c, qty: n, qtyDraft: String(n) };
      })
    );
  };

  // ======= Auto-lines (como OrdersForm) =======
  const autoLines = useMemo(() => {
    const lines = [];
    const condRank = { estreno: 3, media: 2, baja: 1 };
    for (const ch of selectedChars) {
      const times = Math.max(0, Number(ch.qty) || 0);
      if (!times) continue;
      const items = charItemsCache[ch.id] || [];
      const perGarment = new Map();
      items.forEach((it, idx) => {
        const gid = it.garment_id || it?.garment?.id;
        if (!gid) return;
        const rmKey = `${ch.id}:${gid}`;
        if (removedFromAuto.has(rmKey)) return;
        const reqQty = Math.max(0, Number(it.required_qty || it.qty || 0));
        const cond = it.min_condition || 'media';
        const size = it.required_size ?? it.size ?? '';
        const score = reqQty * 1000 + (condRank[cond] ?? 0) * 10 + (size ? 1 : 0) - idx * 1e-6;
        const existing = perGarment.get(gid);
        if (!existing || score > existing.score) perGarment.set(gid, { it, score, reqQty });
      });
      for (const [gid, { it, reqQty }] of perGarment) {
        const addQty = reqQty * times;
        if (!addQty) continue;
        const cond = it.min_condition || 'media';
        const size = it.required_size ?? it.size ?? '';
        lines.push({
          source: 'auto',
          key: `auto-${ch.id}-${gid}`,
          rmKey: `${ch.id}:${gid}`,
          garment_id: gid,
          name: it?.garment?.name ?? it?.name ?? '',
          color: it?.garment?.color ?? it?.color ?? '',
          condition: cond,
          size,
          qty: addQty,
          character_id: ch.id,
          character_name: ch.name,
          dance_id: ch.dance_id ?? null,
          dance_name: ch.dance_name ?? 'Sin danza'
        });
      }
    }
    return lines;
  }, [selectedChars, charItemsCache, removedFromAuto]);

  const [finalLines, setFinalLines] = useState([]);

  useEffect(() => {
    setFinalLines((prev) => {
      const prevByKey = new Map(prev.map((l) => [l.key, l]));
      return [...autoLines, ...extraLines].map((l) => {
        const old = prevByKey.get(l.key);
        if (!old) return l;
        const isAuto = l.source === 'auto';
        return {
          ...l,
          unit_price: old.manualPrice ? old.unit_price : l.unit_price,
          manualPrice: old.manualPrice ?? l.manualPrice,
          size: old.size ?? l.size,
          condition: old.condition ?? l.condition,
          qty: isAuto ? l.qty : old.qty ?? l.qty,
          qtyDraft: isAuto ? String(l.qty ?? 0) : old.qtyDraft ?? String(old.qty ?? l.qty ?? 0)
        };
      });
    });
  }, [autoLines, extraLines]);

  // ======= Disponibilidad (igual que OrdersForm) =======
  const normalizeAvailability = (data) => {
    const conditions = Array.isArray(data?.available_conditions) ? data.available_conditions : undefined;
    const stockBySize =
      data?.stock_by_size && typeof data.stock_by_size === 'object' ? data.stock_by_size : null;
    const stockByCondition =
      data?.stock_by_condition && typeof data.stock_by_condition === 'object'
        ? data.stock_by_condition
        : null;
    const stockByConditionSize =
      data?.stock_by_condition_size && typeof data.stock_by_condition_size === 'object'
        ? data.stock_by_condition_size
        : null;
    const availableByConditionSize =
      data?.available_by_condition_size && typeof data.available_by_condition_size === 'object'
        ? data.available_by_condition_size
        : null;
    const rentedByConditionSize =
      data?.rented_by_condition_size && typeof data.rented_by_condition_size === 'object'
        ? data.rented_by_condition_size
        : null;

    const availableBySize = availableByConditionSize
      ? Object.keys(availableByConditionSize).reduce((acc, cond) => {
          Object.entries(availableByConditionSize[cond] || {}).forEach(([sz, qty]) => {
            acc[sz] = (acc[sz] || 0) + Number(qty || 0);
          });
          return acc;
        }, {})
      : null;

    const sizes = Array.isArray(data?.sizes)
      ? data.sizes
      : Array.isArray(data?.available_sizes)
      ? data.available_sizes
      : stockBySize
      ? Object.keys(stockBySize)
      : [];

    const stockTotal = Number(
      data?.stock_total ??
        data?.available_qty ??
        (stockBySize ? Object.values(stockBySize).reduce((a, b) => a + Number(b || 0), 0) : 0)
    );

    const priceByConditionSize =
      data?.price_by_condition_size && typeof data.price_by_condition_size === 'object'
        ? data.price_by_condition_size
        : null;

    return {
      conditions: conditions && conditions.length ? conditions : ['estreno', 'media', 'baja'],
      sizes: sizes || [],
      stockTotal,
      stockBySize,
      stockByCondition,
      stockByConditionSize,
      availableByConditionSize,
      availableBySize,
      rentedByConditionSize,
      best_condition: data?.best_condition || null,
      priceByConditionSize
    };
  };

  // Tallas válidas para una prenda+condición (igual que OrdersForm)
  function getSizeOptionsForLine(garmentId, condition) {
    if (!garmentId) return [];
    const av = availabilityCache[garmentId];
    if (!av) return [];

    const cond = condition || null;

    const fromMap = (map) => {
      if (!cond || !map || typeof map !== 'object' || !map[cond]) return [];
      return Object.entries(map[cond])
        .filter(([, qty]) => Number(qty || 0) > 0)
        .map(([size]) => size);
    };

    // 1) Preferimos tallas con disponibilidad real
    let sizes = fromMap(av.availableByConditionSize);
    if (!sizes.length) {
      // 2) Si no hay "available", usamos stock por condición/talla
      sizes = fromMap(av.stockByConditionSize);
    }

    // 3) Fallbacks
    if (sizes.length) return sizes;
    if (Array.isArray(av.sizes) && av.sizes.length) return av.sizes;
    if (av.stockBySize && typeof av.stockBySize === 'object') {
      return Object.keys(av.stockBySize);
    }

    return [];
  }

  const fetchAvailability = useCallback(
    async (garmentId) => {
      if (!garmentId) return null;
      if (availabilityCache[garmentId]) return availabilityCache[garmentId];
      try {
        const { data } = await http.get(`/garments/${garmentId}/availability`);
        const norm = normalizeAvailability(data);
        setAvailabilityCache((prev) => ({ ...prev, [garmentId]: norm }));
        return norm;
      } catch {
        const fallback = normalizeAvailability({});
        setAvailabilityCache((prev) => ({ ...prev, [garmentId]: fallback }));
        return fallback;
      }
    },
    [availabilityCache]
  );

  useEffect(() => {
    const ids = new Set(finalLines.map((l) => l.garment_id).filter(Boolean));
    ids.forEach((gid) => {
      if (!availabilityCache[gid]) fetchAvailability(gid);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalLines]);

  const extraOnly = useMemo(
    () => finalLines.filter((l) => l.source === 'extra' && !l.inline),
    [finalLines]
  );

  const computeMaxQty = (line) => {
    const av = availabilityCache[line.garment_id];
    if (!av) return undefined;
    if (av.availableByConditionSize && line.condition && line.size)
      return Number(av.availableByConditionSize?.[line.condition]?.[line.size] ?? 0);
    if (av.availableBySize && line.size) return Number(av.availableBySize?.[line.size] ?? 0);
    if (av.stockByConditionSize && line.condition && line.size)
      return Number(av.stockByConditionSize?.[line.condition]?.[line.size] ?? 0);
    if (av.stockBySize && line.size) return Number(av.stockBySize?.[line.size] ?? 0);
    if (av.stockByCondition && line.condition)
      return Number(av.stockByCondition?.[line.condition] ?? av.stockTotal ?? 0);
    return av.stockTotal ?? undefined;
  };

  const removeLine = (line) => {
    if (line.source === 'auto') {
      setRemovedFromAuto((prev) => new Set([...prev, line.rmKey]));
      setExtraLines((prev) => prev.filter((x) => !(x.inline && x.parentKey === line.key)));
    } else {
      setExtraLines((prev) => prev.filter((x) => x.tempId !== line.tempId));
    }
  };

  const updateLine = (key, patch) => {
    setFinalLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
    const target = finalLines.find((l) => l.key === key);
    if (target?.source === 'extra') {
      setExtraLines((prev) => prev.map((l) => (l.tempId === target.tempId ? { ...l, ...patch } : l)));
    }
  };

  const safeUpdateLine = (key, patch) => {
    setFinalLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        const next = { ...l, ...patch };
        const av = availabilityCache[next.garment_id];
        if (av) {
          // Condición válida
          if (av.conditions && !av.conditions.includes(next.condition)) {
            next.condition = av.conditions[0] || 'media';
          }

          // Talla válida para esa condición
          const sizeOptions = getSizeOptionsForLine(next.garment_id, next.condition);
          if (sizeOptions.length) {
            if (!next.size || !sizeOptions.includes(next.size)) {
              next.size = sizeOptions[0];
            }
          } else {
            next.size = next.size || '';
          }

          const changedSizeOrCond = 'size' in patch || 'condition' in patch;
          if (changedSizeOrCond && !next.manualPrice) {
            const pmap = av.priceByConditionSize?.[next.condition] || {};
            const suggested = Number(
              (next.size && pmap[next.size] != null)
                ? pmap[next.size]
                : Object.values(pmap ?? {})[0] ?? 0
            );
            next.unit_price = suggested;
          }

          const max = computeMaxQty(next);
          if (typeof max === 'number') {
            const n = Number(next.qty);
            next.qty = isNaN(n) ? 0 : Math.max(0, Math.min(n, max));
          }
          next.qtyDraft = String(next.qty ?? 0);
        }
        return next;
      })
    );
    const target = finalLines.find((l) => l.key === key);
    if (target?.source === 'extra') {
      setExtraLines((prev) => prev.map((l) => (l.tempId === target.tempId ? { ...l, ...patch } : l)));
    }
  };

  const handleQtyChange = (key, raw) => {
    const onlyDigits = (raw || '').replace(/[^\d]/g, '');
    updateLine(key, { qtyDraft: onlyDigits });
  };

  const handleQtyCommit = (key) => {
    const l = finalLines.find((x) => x.key === key);
    if (!l) return;
    const raw = (l.qtyDraft ?? '').trim();
    let n = raw === '' ? 0 : parseInt(raw, 10);
    if (isNaN(n)) n = 0;
    const max = computeMaxQty(l);
    if (typeof max === 'number') n = Math.min(n, max);
    if (n < 0) n = 0;
    safeUpdateLine(key, { qty: n, qtyDraft: String(n) });
  };

  const getSuggestedPrice = (line) => {
    const av = availabilityCache[line.garment_id];
    const pmap = av?.priceByConditionSize?.[line.condition] || {};
    const s = line.size && pmap[line.size] != null ? Number(pmap[line.size]) : Number(Object.values(pmap ?? {})[0] ?? 0);
    return Number.isFinite(s) ? s : 0;
  };

  const getUnitPrice = (line) => {
    const p = line.unit_price != null ? Number(line.unit_price) : getSuggestedPrice(line);
    return Number.isFinite(p) ? p : 0;
  };

  const getLineSubtotal = (line) => getUnitPrice(line) * (Number(line.qty) || 0);

  const totalLines = useMemo(
    () => finalLines.reduce((acc, l) => acc + getLineSubtotal(l), 0),
    [finalLines, availabilityCache]
  );

  useEffect(() => {
    setTotal(round2(totalLines));
  }, [totalLines]);

  // Agrupado Danza -> Personaje
  const groupedByDance = useMemo(() => {
    const map = new Map();
    for (const l of finalLines) {
      if (!(l.source === 'auto' || (l.source === 'extra' && l.inline))) continue;
      const dKey = `${l.dance_id ?? '0'}|${l.dance_name ?? 'Sin danza'}`;
      if (!map.has(dKey))
        map.set(dKey, {
          dance_id: l.dance_id,
          dance_name: l.dance_name ?? 'Sin danza',
          characters: new Map()
        });
      const d = map.get(dKey);
      const cKey = `${l.character_id}|${l.character_name}`;
      if (!d.characters.has(cKey))
        d.characters.set(cKey, {
          character_id: l.character_id,
          character_name: l.character_name,
          auto: [],
          inlineByParent: new Map()
        });
      const cg = d.characters.get(cKey);
      if (l.source === 'auto') cg.auto.push(l);
      else if (l.inline && l.parentKey) {
        if (!cg.inlineByParent.has(l.parentKey)) cg.inlineByParent.set(l.parentKey, []);
        cg.inlineByParent.get(l.parentKey).push(l);
      }
    }
    return Array.from(map.values()).map((d) => ({
      dance_id: d.dance_id,
      dance_name: d.dance_name,
      characters: Array.from(d.characters.values()).map((cg) => ({
        character_id: cg.character_id,
        character_name: cg.character_name,
        lines: cg.auto.flatMap((al) => [al, ...(cg.inlineByParent.get(al.key) || [])])
      }))
    }));
  }, [finalLines]);

  // ======= Garments búsqueda manual =======
  const [gSearch, setGSearch] = useState('');
  const [gOptions, setGOptions] = useState([]);
  const [gLoading, setGLoading] = useState(false);

  const fetchGarments = useCallback(async (q = '') => {
    try {
      setGLoading(true);
      const { data } = await http.get('/garments', { params: { q } });
      setGOptions(Array.isArray(data) ? data : []);
    } catch {
      setGOptions([]);
    } finally {
      setGLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGarments('');
  }, [fetchGarments]);

  const addManualGarment = (g) => {
    if (!g?.id) return;
    if (autoLines.some((l) => l.garment_id === g.id)) return;
    const existing = extraLines.find((l) => l.garment_id === g.id);
    if (existing) {
      setExtraLines((prev) =>
        prev.map((l) => (l.garment_id === g.id ? { ...l, qty: (Number(l.qty) || 0) + 1 } : l))
      );
      return;
    }
    const tempId = `x-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    (async () => {
      const avail = await fetchAvailability(g.id);
      const defaultCond = avail?.best_condition || (avail?.conditions?.[0] ?? 'media');
      const defaultSize = avail?.sizes && avail.sizes.length ? avail.sizes[0] : '';
      const pmap = avail?.priceByConditionSize?.[defaultCond] || {};
      const defaultPrice = Number(
        defaultSize && pmap[defaultSize] != null ? pmap[defaultSize] : Object.values(pmap ?? {})[0] ?? 0
      );
      setExtraLines((prev) => [
        ...prev,
        {
          source: 'extra',
          tempId,
          key: `extra-${tempId}`,
          garment_id: g.id,
          name: g.name,
          color: g.color || '',
          condition: defaultCond,
          size: defaultSize,
          qty: 1,
          qtyDraft: '1',
          unit_price: defaultPrice,
          manualPrice: false
        }
      ]);
    })();
  };

  const duplicateLine = (line) => {
    const tempId = `dup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setExtraLines((prev) => [
      ...prev,
      {
        source: 'extra',
        tempId,
        key: `extra-${tempId}`,
        garment_id: line.garment_id,
        name: line.name,
        color: line.color || '',
        condition: line.condition || 'media',
        size: line.size || '',
        qty: Number(line.qty || 0),
        qtyDraft: String(line.qty || 0),
        unit_price: line.unit_price,
        manualPrice: true
      }
    ]);
  };

  // ======= Submit =======
  const handleSubmit = async (e) => {
    e.preventDefault();

    const characters = selectedChars
      .map((c) => ({
        character_id: c.id,
        qty: Number(c.qty ?? c.qtyDraft ?? 0) || 0
      }))
      .filter((c) => c.qty > 0);

    const items = finalLines
      .filter((l) => (Number(l.qty) || 0) > 0)
      .map((l) => {
        const price = getUnitPrice(l);
        return {
          garment_id: l.garment_id,
          character_id: l.character_id ?? null,
          size_label: (l.size || '').trim() || null,
          condition: l.condition || 'media',
          qty: Number(l.qty) || 0,
          unit_price: round2(Number.isFinite(price) ? price : 0),
          source: l.source === 'auto' ? 'auto' : 'manual'
        };
      });

    if (!deliveryDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Falta la fecha de separación',
        text: 'Selecciona la fecha de separación.'
      });
      return;
    }

    if (!items.length) {
      Swal.fire({
        icon: 'warning',
        title: 'Sin prendas',
        text: 'Agrega al menos una prenda con cantidad mayor a 0.'
      });
      return;
    }

    const payload = {
      client: {
        dni,
        first_name: fullName,
        full_name: fullName,
        phone: contactNumber,
        address,
        last_name_father: lastNameFather,
        last_name_mother: lastNameMother,
        references
      },
      reservation_date: deliveryDate.format('YYYY-MM-DD'),
      notes: references || null,
      characters,
      items
    };

    try {
      await http.post('/reservations', payload);
      Swal.fire({
        icon: 'success',
        title: 'Separación creada',
        timer: 1800,
        showConfirmButton: false
      });
      navigate('/reservations');
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text:
          'No se pudo guardar la separación: ' +
          (err?.response?.data?.message || err?.message || String(err))
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 1500, ...UPPER_INPUT_SX }}>
      {/* Header con flecha de regreso */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton
          aria-label="Volver"
          onClick={() => navigate(-1)}
          sx={{ mr: 1 }}
        >
          <ArrowLeftOutlined />
        </IconButton>
        <Typography variant="h5">Nueva Separación</Typography>
      </Box>

      <Grid
        container
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr' },
          gap: 2,
          '& > *': { width: '100%' },
          '& .MuiFormControl-root, & .MuiTextField-root': { m: 0 }
        }}
      >
        <TextField
          label="N° de Separación (se asignará al guardar)"
          value={orderNumberPreview || orderNumber}
          disabled
          autoComplete="off"
        />

        <TextField
          label="N° de DNI"
          value={dni}
          onChange={(e) => {
            setDni(e.target.value);
            setClientAlert(null);
          }}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 12 }}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSearchDni();
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="Buscar por DNI" onClick={handleSearchDni} disabled={searching}>
                  <SearchOutlined />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <TextField
          label="Nombres Completos"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="off"
        />
        <TextField
          label="Apellido Paterno"
          value={lastNameFather}
          onChange={(e) => setLastNameFather(e.target.value)}
          autoComplete="off"
        />
        <TextField
          label="Apellido Materno"
          value={lastNameMother}
          onChange={(e) => setLastNameMother(e.target.value)}
          autoComplete="off"
        />
        <TextField
          label="Celular"
          value={contactNumber}
          onChange={(e) => {
            const digits = (e.target.value || '').replace(/[^\d]/g, '').slice(0, 9);
            setContactNumber(digits);
          }}
          autoComplete="tel"
          inputProps={{ inputMode: 'tel', pattern: '[0-9]*', maxLength: 9 }}
          InputProps={{ startAdornment: <InputAdornment position="start">+51</InputAdornment> }}
          error={Boolean(contactNumber) && contactNumber.length !== 9}
        />
        <TextField
          label="Dirección"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          autoComplete="off"
        />
        <TextField
          label="Referencias"
          value={references}
          onChange={(e) => setReferences(e.target.value)}
          autoComplete="off"
        />

        {/* ======= Personajes seleccionados ======= */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              variant="outlined"
              startIcon={<PlusOutlined />}
              onClick={() => setPickerOpen(true)}
            >
              Agregar por danza
            </Button>
          </Stack>
        </Box>

        {/* ======= Lista de prendas agrupadas por Danza -> Personaje ======= */}
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Collapse in={showCharacters} unmountOnExit>
            {groupedByDance.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay prendas derivadas de personajes seleccionados.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {groupedByDance.map((dg, idxD) => (
                  <Accordion
                    key={`dance-${idxD}`}
                    defaultExpanded
                    disableGutters
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:before': { display: 'none' },
                      overflow: 'hidden'
                    }}
                  >
                    {/* Cabecera de DANZA */}
                    <AccordionSummary
                      expandIcon={<DownOutlined />}
                      sx={{
                        bgcolor: 'action.hover',
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          my: 0.5
                        }
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>
                        Danza: {dg.dance_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dg.characters.length} personaje(s)
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 1.25 }}>
                      <Stack spacing={1.5}>
                        {dg.characters.map((cg) => {
                          const cState =
                            selectedChars.find((c) => c.id === cg.character_id) || {
                              id: cg.character_id,
                              name: cg.character_name,
                              dance_name: dg.dance_name,
                              qty: 0,
                              qtyDraft: '0'
                            };

                          const charKey = `${dg.dance_id ?? '0'}-${cg.character_id}`;

                          return (
                            <Accordion
                              key={`char-${charKey}`}
                              expanded={isCharOpen(charKey)}
                              onChange={handleCharAccordion(charKey)}
                              disableGutters
                              sx={{
                                borderRadius: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                '&:before': { display: 'none' },
                                overflow: 'hidden'
                              }}
                            >
                              {/* Cabecera de PERSONAJE */}
                              <AccordionSummary
                                expandIcon={<DownOutlined />}
                                sx={{
                                  bgcolor: 'background.paper',
                                  '& .MuiAccordionSummary-content': {
                                    alignItems: 'center',
                                    my: 0.5
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
                                    gap: 1,
                                    alignItems: 'center',
                                    width: '100%'
                                  }}
                                >
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    Personaje: {cState.name} ({dg.dance_name})
                                  </Typography>
                                  {cState.quality && (
                                    <Chip
                                      size="small"
                                      label={
                                        `Calidad: ${(QUALITY.find((q) => q.value === cState.quality)?.label) || cState.quality}`
                                      }
                                      color="info"
                                    />
                                  )}
                                  <Chip
                                    size="small"
                                    label={`Cant. personajes: ${cState.qty ?? 0}`}
                                  />
                                  <IconButton
                                    aria-label="Quitar personaje"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeCharacter(cState.id);
                                    }}
                                    sx={{ justifySelf: 'end' }}
                                  >
                                    <DeleteOutlined />
                                  </IconButton>
                                </Box>
                              </AccordionSummary>

                              {/* Detalle de PERSONAJE: cantidad editable + prendas */}
                              <AccordionDetails sx={{ p: 1.25 }}>
                                <Box
                                  sx={{
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    p: 1
                                  }}
                                >
                                  {/* Cabecera del personaje + cantidad */}
                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gap: 1,
                                      gridTemplateColumns: '1fr 120px',
                                      alignItems: 'center',
                                      mb: 1
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      label="Personaje"
                                      value={`${cState.name} (${dg.dance_name})`}
                                      disabled
                                    />
                                    <TextField
                                      size="small"
                                      label="Cant. personajes"
                                      type="text"
                                      inputMode="numeric"
                                      pattern="\d*"
                                      value={
                                        cState.qtyDraft ??
                                        (cState.qty != null ? String(cState.qty) : '')
                                      }
                                      onChange={(e) => {
                                        const onlyDigits = (e.target.value || '').replace(
                                          /[^\d]/g,
                                          ''
                                        );
                                        handleCharQtyChange(cState.id, onlyDigits);
                                      }}
                                      onBlur={() => handleCharQtyCommit(cState.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleCharQtyCommit(cState.id);
                                        }
                                      }}
                                    />
                                  </Box>

                                  {/* Lista de prendas de este personaje */}
                                  <Stack spacing={1.25}>
                                    {cg.lines.map((l) => (
                                      <Box
                                        key={l.key}
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          borderRadius: 1,
                                          p: 1
                                        }}
                                      >
                                        {/* XS: 2 filas */}
                                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                                          {/* Fila 1 XS: Cantidad | Prenda | Talla | Calidad */}
                                          <Grid
                                            container
                                            sx={{
                                              display: 'grid',
                                              gap: 0.5,
                                              alignItems: 'center',
                                              gridTemplateColumns:
                                                '70px minmax(150px,2fr) 1.1fr 1.1fr'
                                            }}
                                          >
                                            {/* Cantidad */}
                                            {(() => {
                                              const max = computeMaxQty(l);
                                              const display =
                                                l.qtyDraft ??
                                                (l.qty != null ? String(l.qty) : '');
                                              return (
                                                <TextField
                                                  size="small"
                                                  sx={COMPACT_INPUT_SX}
                                                  label={`Disp. ${max}`}
                                                  type="text"
                                                  inputMode="numeric"
                                                  pattern="\d*"
                                                  value={display}
                                                  onChange={(e) =>
                                                    handleQtyChange(l.key, e.target.value)
                                                  }
                                                  onBlur={() => handleQtyCommit(l.key)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                      e.preventDefault();
                                                      handleQtyCommit(l.key);
                                                    }
                                                  }}
                                                  placeholder="0"
                                                />
                                              );
                                            })()}

                                            {/* Prenda */}
                                            <TextField
                                              size="small"
                                              sx={COMPACT_INPUT_SX}
                                              label="Prenda (desde personaje)"
                                              value={`${l.name}${l.color ? ` · ${l.color}` : ''}`}
                                              disabled
                                            />

                                            {/* Talla */}
                                            {(() => {
                                              const sizeOptions = getSizeOptionsForLine(
                                                l.garment_id,
                                                l.condition
                                              );
                                              return sizeOptions.length > 0 ? (
                                                <FormControl
                                                  fullWidth
                                                  size="small"
                                                  sx={COMPACT_INPUT_SX}
                                                >
                                                  <InputLabel id={`size-xs-${l.key}`}>
                                                    Talla
                                                  </InputLabel>
                                                  <Select
                                                    labelId={`size-xs-${l.key}`}
                                                    label="Talla"
                                                    value={l.size ?? ''}
                                                    onChange={(e) =>
                                                      safeUpdateLine(l.key, {
                                                        size: e.target.value
                                                      })
                                                    }
                                                  >
                                                    {sizeOptions.map((sz) => (
                                                      <MenuItem key={sz} value={sz}>
                                                        {sz}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                              ) : (
                                                <TextField
                                                  size="small"
                                                  sx={COMPACT_INPUT_SX}
                                                  label="Talla"
                                                  value={l.size}
                                                  onChange={(e) =>
                                                    safeUpdateLine(l.key, {
                                                      size: e.target.value
                                                    })
                                                  }
                                                />
                                              );
                                            })()}

                                            {/* Calidad */}
                                            {(() => {
                                              const av = availabilityCache[l.garment_id];
                                              const allowed =
                                                av?.conditions ??
                                                QUALITY.map((q) => q.value);
                                              return (
                                                <FormControl
                                                  fullWidth
                                                  size="small"
                                                  sx={COMPACT_INPUT_SX}
                                                >
                                                  <InputLabel id={`cond-xs-${l.key}`}>
                                                    Calidad
                                                  </InputLabel>
                                                  <Select
                                                    labelId={`cond-xs-${l.key}`}
                                                    label="Calidad"
                                                    value={l.condition}
                                                    onChange={(e) =>
                                                      safeUpdateLine(l.key, {
                                                        condition: e.target.value
                                                      })
                                                    }
                                                  >
                                                    {QUALITY.filter((q) =>
                                                      allowed.includes(q.value)
                                                    ).map((q) => (
                                                      <MenuItem
                                                        key={q.value}
                                                        value={q.value}
                                                      >
                                                        {q.label}
                                                      </MenuItem>
                                                    ))}
                                                  </Select>
                                                </FormControl>
                                              );
                                            })()}
                                          </Grid>

                                          {/* Fila 2 XS: Precio | Subtotal | Copiar | Eliminar */}
                                          <Grid
                                            container
                                            sx={{
                                              mt: 0.5,
                                              display: 'grid',
                                              gap: 0.5,
                                              alignItems: 'center',
                                              gridTemplateColumns:
                                                '1.2fr 1.2fr 36px 36px'
                                            }}
                                          >
                                            {/* Precio */}
                                            {(() => {
                                              const av = availabilityCache[l.garment_id];
                                              const pmap =
                                                av?.priceByConditionSize?.[
                                                  l.condition
                                                ] || {};
                                              const suggested =
                                                l.size && pmap[l.size] != null
                                                  ? Number(pmap[l.size])
                                                  : Number(
                                                      Object.values(pmap ?? {})[0] ??
                                                        0
                                                    );
                                              return (
                                                <TextField
                                                  size="small"
                                                  sx={COMPACT_INPUT_SX}
                                                  label="Precio"
                                                  type="number"
                                                  inputProps={{
                                                    min: 0,
                                                    step: '0.01',
                                                    inputMode: 'decimal'
                                                  }}
                                                  value={l.unit_price ?? ''}
                                                  placeholder={String(
                                                    suggested ?? ''
                                                  )}
                                                  onFocus={(e) =>
                                                    e.target.select()
                                                  }
                                                  onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === '') {
                                                      safeUpdateLine(l.key, {
                                                        unit_price: undefined,
                                                        manualPrice: false
                                                      });
                                                      return;
                                                    }
                                                    const normalized =
                                                      raw.replace(',', '.');
                                                    const num =
                                                      Number(normalized);
                                                    if (
                                                      Number.isFinite(num) &&
                                                      num >= 0
                                                    ) {
                                                      safeUpdateLine(l.key, {
                                                        unit_price: num,
                                                        manualPrice: true
                                                      });
                                                    }
                                                  }}
                                                  onBlur={() => {
                                                    const line = finalLines.find(
                                                      (x) => x.key === l.key
                                                    );
                                                    if (
                                                      line?.unit_price != null
                                                    ) {
                                                      safeUpdateLine(l.key, {
                                                        unit_price:
                                                          Math.round(
                                                            Number(
                                                              line.unit_price
                                                            ) * 100
                                                          ) / 100,
                                                        manualPrice: true
                                                      });
                                                    }
                                                  }}
                                                />
                                              );
                                            })()}

                                            {/* Subtotal */}
                                            <TextField
                                              size="small"
                                              sx={COMPACT_INPUT_SX}
                                              label="Subtotal"
                                              value={getLineSubtotal(l).toFixed(2)}
                                              InputProps={{ readOnly: true }}
                                            />

                                            {/* Duplicar */}
                                            <IconButton
                                              size="small"
                                              aria-label="Duplicar"
                                              color="primary"
                                              onClick={() => duplicateLine(l)}
                                              sx={{
                                                width: FIELD_HEIGHT,
                                                height: FIELD_HEIGHT,
                                                justifySelf: 'center'
                                              }}
                                            >
                                              <CopyOutlined />
                                            </IconButton>

                                            {/* Eliminar */}
                                            <IconButton
                                              size="small"
                                              aria-label="Eliminar"
                                              color="error"
                                              onClick={() => removeLine(l)}
                                              sx={{
                                                width: FIELD_HEIGHT,
                                                height: FIELD_HEIGHT,
                                                justifySelf: 'center'
                                              }}
                                            >
                                              <DeleteOutlined />
                                            </IconButton>
                                          </Grid>
                                        </Box>

                                        {/* MD+: fila única */}
                                        <Grid
                                          container
                                          sx={{
                                            display: { xs: 'none', md: 'grid' },
                                            gap: 0.5,
                                            alignItems: 'center',
                                            gridTemplateColumns:
                                              '92px minmax(300px,1fr) 110px 120px 110px 120px 36px 36px'
                                          }}
                                        >
                                          {/* Cantidad */}
                                          {(() => {
                                            const max = computeMaxQty(l);
                                            const display =
                                              l.qtyDraft ??
                                              (l.qty != null ? String(l.qty) : '');
                                            return (
                                              <TextField
                                                size="small"
                                                sx={COMPACT_INPUT_SX}
                                                label={`Disp. ${max}`}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="\d*"
                                                value={display}
                                                onChange={(e) =>
                                                  handleQtyChange(
                                                    l.key,
                                                    e.target.value
                                                  )
                                                }
                                                onBlur={() =>
                                                  handleQtyCommit(l.key)
                                                }
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleQtyCommit(l.key);
                                                  }
                                                }}
                                                placeholder="0"
                                              />
                                            );
                                          })()}

                                          {/* Prenda */}
                                          <TextField
                                            size="small"
                                            sx={COMPACT_INPUT_SX}
                                            label="Prenda (desde personaje)"
                                            value={`${l.name}${
                                              l.color ? ` · ${l.color}` : ''
                                            }`}
                                            disabled
                                          />

                                          {/* Talla */}
                                          {(() => {
                                            const sizeOptions =
                                              getSizeOptionsForLine(
                                                l.garment_id,
                                                l.condition
                                              );
                                            return sizeOptions.length > 0 ? (
                                              <FormControl
                                                fullWidth
                                                size="small"
                                                sx={COMPACT_INPUT_SX}
                                              >
                                                <InputLabel
                                                  id={`size-md-${l.key}`}
                                                >
                                                  Talla
                                                </InputLabel>
                                                <Select
                                                  labelId={`size-md-${l.key}`}
                                                  label="Talla"
                                                  value={l.size ?? ''}
                                                  onChange={(e) =>
                                                    safeUpdateLine(l.key, {
                                                      size: e.target.value
                                                    })
                                                  }
                                                >
                                                  {sizeOptions.map((sz) => (
                                                    <MenuItem
                                                      key={sz}
                                                      value={sz}
                                                    >
                                                      {sz}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                              </FormControl>
                                            ) : (
                                              <TextField
                                                size="small"
                                                sx={COMPACT_INPUT_SX}
                                                label="Talla"
                                                value={l.size}
                                                onChange={(e) =>
                                                  safeUpdateLine(l.key, {
                                                    size: e.target.value
                                                  })
                                                }
                                              />
                                            );
                                          })()}

                                          {/* Calidad */}
                                          {(() => {
                                            const av =
                                              availabilityCache[l.garment_id];
                                            const allowed =
                                              av?.conditions ??
                                              QUALITY.map((q) => q.value);
                                            return (
                                              <FormControl
                                                fullWidth
                                                size="small"
                                                sx={COMPACT_INPUT_SX}
                                              >
                                                <InputLabel
                                                  id={`cond-md-${l.key}`}
                                                >
                                                  Calidad
                                                </InputLabel>
                                                <Select
                                                  labelId={`cond-md-${l.key}`}
                                                  label="Calidad"
                                                  value={l.condition}
                                                  onChange={(e) =>
                                                    safeUpdateLine(l.key, {
                                                      condition: e.target.value
                                                    })
                                                  }
                                                >
                                                  {QUALITY.filter((q) =>
                                                    allowed.includes(q.value)
                                                  ).map((q) => (
                                                    <MenuItem
                                                      key={q.value}
                                                      value={q.value}
                                                    >
                                                      {q.label}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                              </FormControl>
                                            );
                                          })()}

                                          {/* Precio */}
                                          {(() => {
                                            const av = availabilityCache[l.garment_id];
                                            const pmap =
                                              av?.priceByConditionSize?.[
                                                l.condition
                                              ] || {};
                                            const suggested =
                                              l.size && pmap[l.size] != null
                                                ? Number(pmap[l.size])
                                                : Number(
                                                    Object.values(pmap ?? {})[0] ??
                                                      0
                                                  );
                                            return (
                                              <TextField
                                                size="small"
                                                sx={COMPACT_INPUT_SX}
                                                label="Precio"
                                                type="number"
                                                inputProps={{
                                                  min: 0,
                                                  step: '0.01',
                                                  inputMode: 'decimal'
                                                }}
                                                value={l.unit_price ?? ''}
                                                placeholder={String(
                                                  suggested ?? ''
                                                )}
                                                onFocus={(e) =>
                                                  e.target.select()
                                                }
                                                onChange={(e) => {
                                                  const raw = e.target.value;
                                                  if (raw === '') {
                                                    safeUpdateLine(l.key, {
                                                      unit_price: undefined,
                                                      manualPrice: false
                                                    });
                                                    return;
                                                  }
                                                  const normalized =
                                                    raw.replace(',', '.');
                                                  const num =
                                                    Number(normalized);
                                                  if (
                                                    Number.isFinite(num) &&
                                                    num >= 0
                                                  ) {
                                                    safeUpdateLine(l.key, {
                                                      unit_price: num,
                                                      manualPrice: true
                                                    });
                                                  }
                                                }}
                                                onBlur={() => {
                                                  const line = finalLines.find(
                                                    (x) => x.key === l.key
                                                  );
                                                  if (line?.unit_price != null) {
                                                    safeUpdateLine(l.key, {
                                                      unit_price:
                                                        Math.round(
                                                          Number(
                                                            line.unit_price
                                                          ) * 100
                                                        ) / 100,
                                                      manualPrice: true
                                                    });
                                                  }
                                                }}
                                              />
                                            );
                                          })()}

                                          {/* Subtotal */}
                                          <TextField
                                            size="small"
                                            sx={COMPACT_INPUT_SX}
                                            label="Subtotal"
                                            value={getLineSubtotal(l).toFixed(2)}
                                            InputProps={{ readOnly: true }}
                                          />

                                          {/* Duplicar */}
                                          <IconButton
                                            size="small"
                                            aria-label="Duplicar"
                                            color="primary"
                                            onClick={() => duplicateLine(l)}
                                            sx={{
                                              width: FIELD_HEIGHT,
                                              height: FIELD_HEIGHT,
                                              justifySelf: 'center'
                                            }}
                                          >
                                            <CopyOutlined />
                                          </IconButton>

                                          {/* Eliminar */}
                                          <IconButton
                                            size="small"
                                            aria-label="Eliminar"
                                            color="error"
                                            onClick={() => removeLine(l)}
                                            sx={{
                                              width: FIELD_HEIGHT,
                                              height: FIELD_HEIGHT,
                                              justifySelf: 'center'
                                            }}
                                          >
                                            <DeleteOutlined />
                                          </IconButton>
                                        </Grid>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            )}
          </Collapse>

          {/* Autocomplete para prendas manuales */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ mr: 'auto' }}>
              Prendas
            </Typography>

            <Autocomplete
              sx={{ minWidth: 360 }}
              options={gOptions}
              loading={gLoading}
              filterOptions={(x) => x}
              inputValue={gSearch}
              onInputChange={(_, text, reason) => {
                if (reason === 'input') {
                  setGSearch(text);
                  fetchGarments(text);
                }
                if (reason === 'clear') {
                  setGSearch('');
                  fetchGarments('');
                }
              }}
              onOpen={() => {
                if (!gOptions.length) fetchGarments('');
              }}
              onChange={(_, g) => {
                if (g?.id) {
                  addManualGarment(g);
                  setGSearch('');
                  fetchGarments('');
                }
              }}
              getOptionLabel={(o) => o?.name ?? ''}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              noOptionsText={gLoading ? 'Buscando...' : 'Sin opciones'}
              renderOption={(props, opt) => {
                const dances = Array.isArray(opt?.meta?.dances) ? opt.meta.dances : [];
                const chars = Array.isArray(opt?.meta?.characters) ? opt.meta.characters : [];
                return (
                  <li {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {opt?.name ?? ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt?.sku ? `SKU: ${opt.sku}` : ''}
                        {opt?.sku && opt?.color ? ' • ' : ''}
                        {opt?.color ? `Color: ${opt.color}` : ''}
                      </Typography>
                      {(dances.length > 0 || chars.length > 0) && (
                        <Box
                          sx={{
                            mt: 0.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5
                          }}
                        >
                          {dances.slice(0, 4).map((d) => (
                            <Chip key={`d-${d}`} size="small" label={d} sx={{ mr: 0.5 }} />
                          ))}
                          {chars.slice(0, 4).map((c) => (
                            <Chip
                              key={`c-${c}`}
                              size="small"
                              variant="outlined"
                              label={c}
                              sx={{ mr: 0.5 }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Añadir prenda (nombre / SKU / color / danza / personaje)"
                  placeholder="Escribe para buscar…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                />
              )}
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Prendas adicionales (manual)
          </Typography>

          {extraOnly.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay prendas manuales.
            </Typography>
          ) : (
            <Stack spacing={1.25}>
              {extraOnly.map((l) => (
                <Box
                  key={l.key}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                >
                  <Grid
                    container
                    sx={{
                      display: 'grid',
                      gap: 0.5,
                      alignItems: 'center',
                      gridTemplateColumns: {
                        xs: '88px minmax(180px,1fr) 95px 108px 96px 108px 36px 36px',
                        md: '92px minmax(300px,1fr) 110px 120px 110px 120px 36px 36px'
                      }
                    }}
                  >
                    {/* Cantidad */}
                    {(() => {
                      const max = computeMaxQty(l);
                      const display = l.qtyDraft ?? (l.qty != null ? String(l.qty) : '');
                      return (
                        <TextField
                          size="small"
                          sx={COMPACT_INPUT_SX}
                          label={`Cant.${typeof max === 'number' ? ` (disp. ${max})` : ''}`}
                          type="text"
                          inputMode="numeric"
                          pattern="\d*"
                          value={display}
                          onChange={(e) => handleQtyChange(l.key, e.target.value)}
                          onBlur={() => handleQtyCommit(l.key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleQtyCommit(l.key);
                            }
                          }}
                          placeholder="0"
                        />
                      );
                    })()}

                    {/* Prenda */}
                    <TextField
                      size="small"
                      sx={COMPACT_INPUT_SX}
                      label="Prenda (manual)"
                      value={`${l.name}${l.color ? ` · ${l.color}` : ''}`}
                      disabled
                    />

                    {/* Talla */}
                    {(() => {
                      const sizeOptions = getSizeOptionsForLine(l.garment_id, l.condition);
                      return sizeOptions.length > 0 ? (
                        <FormControl fullWidth size="small" sx={COMPACT_INPUT_SX}>
                          <InputLabel id={`size-${l.key}`}>Talla</InputLabel>
                          <Select
                            labelId={`size-${l.key}`}
                            label="Talla"
                            value={l.size ?? ''}
                            onChange={(e) =>
                              safeUpdateLine(l.key, { size: e.target.value })
                            }
                          >
                            {sizeOptions.map((sz) => (
                              <MenuItem key={sz} value={sz}>
                                {sz}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          sx={COMPACT_INPUT_SX}
                          label="Talla"
                          value={l.size}
                          onChange={(e) =>
                            safeUpdateLine(l.key, { size: e.target.value })
                          }
                        />
                      );
                    })()}

                    {/* Calidad */}
                    {(() => {
                      const av = availabilityCache[l.garment_id];
                      const allowed = av?.conditions ?? QUALITY.map((q) => q.value);
                      return (
                        <FormControl fullWidth size="small" sx={COMPACT_INPUT_SX}>
                          <InputLabel id={`cond-${l.key}`}>Calidad</InputLabel>
                          <Select
                            labelId={`cond-${l.key}`}
                            label="Calidad"
                            value={l.condition}
                            onChange={(e) =>
                              safeUpdateLine(l.key, { condition: e.target.value })
                            }
                          >
                            {QUALITY.filter((q) => allowed.includes(q.value)).map(
                              (q) => (
                                <MenuItem key={q.value} value={q.value}>
                                  {q.label}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      );
                    })()}

                    {/* Precio */}
                    {(() => {
                      const av = availabilityCache[l.garment_id];
                      const pmap = av?.priceByConditionSize?.[l.condition] || {};
                      const suggested =
                        l.size && pmap[l.size] != null
                          ? Number(pmap[l.size])
                          : Number(Object.values(pmap ?? {})[0] ?? 0);
                      return (
                        <TextField
                          size="small"
                          sx={COMPACT_INPUT_SX}
                          label="Precio"
                          type="number"
                          inputProps={{
                            min: 0,
                            step: '0.01',
                            inputMode: 'decimal'
                          }}
                          value={l.unit_price ?? ''}
                          placeholder={String(suggested ?? '')}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              safeUpdateLine(l.key, {
                                unit_price: undefined,
                                manualPrice: false
                              });
                              return;
                            }
                            const normalized = raw.replace(',', '.');
                            const num = Number(normalized);
                            if (Number.isFinite(num) && num >= 0) {
                              safeUpdateLine(l.key, {
                                unit_price: num,
                                manualPrice: true
                              });
                            }
                          }}
                          onBlur={() => {
                            const line = finalLines.find((x) => x.key === l.key);
                            if (line?.unit_price != null) {
                              safeUpdateLine(l.key, {
                                unit_price:
                                  Math.round(Number(line.unit_price) * 100) /
                                  100,
                                manualPrice: true
                              });
                            }
                          }}
                        />
                      );
                    })()}

                    {/* Subtotal */}
                    <TextField
                      size="small"
                      sx={COMPACT_INPUT_SX}
                      label="Subtotal"
                      value={getLineSubtotal(l).toFixed(2)}
                      InputProps={{ readOnly: true }}
                    />

                    {/* Acciones */}
                    <IconButton
                      size="small"
                      aria-label="Duplicar"
                      color="primary"
                      onClick={() => duplicateLine(l)}
                      sx={{
                        width: FIELD_HEIGHT,
                        height: FIELD_HEIGHT,
                        justifySelf: 'center'
                      }}
                    >
                      <CopyOutlined />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Eliminar"
                      color="error"
                      onClick={() => removeLine(l)}
                      sx={{
                        width: FIELD_HEIGHT,
                        height: FIELD_HEIGHT,
                        justifySelf: 'center'
                      }}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  </Grid>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Total prendas: S/ {Number(totalLines).toFixed(2)}
          </Typography>
        </Box>

        {/* ======= Datos de separación ======= */}
        <Divider sx={{ gridColumn: '1 / -1', my: 1.5 }} />
        <Box sx={{ gridColumn: '1 / -1', fontWeight: 600, mb: -1 }}>
          Fecha de separación
        </Box>
        <DatePicker
          label="Fecha de separación"
          value={deliveryDate}
          onChange={(v) => setDeliveryDate(v)}
          format="DD/MM/YYYY"
          slotProps={{ textField: { fullWidth: true, variant: 'outlined', margin: 'none' } }}
          sx={{ width: 1 }}
        />

        {clientAlert && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Alert severity={clientAlert.severity} variant="outlined">
              <Stack spacing={0.25}>
                {clientAlert.lines.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </Stack>
            </Alert>
          </Box>
        )}

        <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="contained" color="primary" type="submit">
            Guardar separación
          </Button>
        </Box>
      </Grid>

      {/* Modal de selección */}
      <CharacterPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickCharacter}
        alreadyIds={new Set(selectedChars.map((c) => c.id))}
      />
    </Box>
  );
}
